<template>
  <div class="ls-page" style="min-height: auto; padding: 0; background: transparent;">
    <div class="ls-wrap" style="width: 100%;">

      <header class="ls-hero" style="margin-bottom: 14px;">
        <div class="ls-hero-grid">
          <div class="ls-hero-main">
            <div class="ls-hero-title">
              PAID INVOICES
              <span class="ls-count-pill">{{ filteredRecords.length }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- FILTERS -->
      <div class="ls-filterstrip" style="background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; padding: 16px; margin-bottom: 14px;">
        <div class="d-flex flex-wrap align-items-end" style="gap: 15px;">

          <div style="width: 260px;">
            <label class="small text-muted mb-1 font-weight-bold">Date Range</label>
            <date-picker
              v-model="selectedDateRange"
              range
              format="MM-DD-YYYY"
              valueType="format"
              :clearable="true"
              :editable="false"
              placeholder="All dates"
              style="width: 100%;"
            ></date-picker>
          </div>

          <div style="width: 160px;">
            <label class="small text-muted mb-1 font-weight-bold">Type</label>
            <b-form-select v-model="selectedType" :options="typeOptions" size="sm"></b-form-select>
          </div>

          <div style="width: 220px;">
            <label class="small text-muted mb-1 font-weight-bold">From (Source)</label>
            <b-form-select v-model="selectedFromLocation" :options="fromLocationOptions" size="sm"></b-form-select>
          </div>

          <div style="width: 220px;">
            <label class="small text-muted mb-1 font-weight-bold">To (Store)</label>
            <b-form-select v-model="selectedToLocation" :options="toLocationOptions" size="sm"></b-form-select>
          </div>

          <!-- 👇 NEW: Payment Receipt # search -->
          <div style="width: 200px;">
            <label class="small text-muted mb-1 font-weight-bold">Receipt #</label>
            <b-form-input
              v-model="searchReceipt"
              placeholder="e.g. PR-2026-000001"
              size="sm"
            ></b-form-input>
          </div>

          <div style="width: 220px;">
            <label class="small text-muted mb-1 font-weight-bold">Invoice #</label>
            <b-form-input
              v-model="searchInvoiceId"
              placeholder="e.g. VN-1777396187"
              size="sm"
            ></b-form-input>
          </div>

          <div style="width: 200px;">
            <label class="small text-muted mb-1 font-weight-bold">Reference #</label>
            <b-form-input
              v-model="searchReference"
              placeholder="e.g. REF-1234"
              size="sm"
            ></b-form-input>
          </div>

          <div>
            <b-button variant="outline-secondary" size="sm" @click="resetFilters">
              <b-icon icon="x"></b-icon> Clear
            </b-button>
          </div>

        </div>
      </div>

      <!-- TABLE -->
      <div v-if="loading" class="text-center p-5 text-muted">
        <b-spinner small></b-spinner> Loading paid invoices…
      </div>
      <div v-else class="ls-card ls-table-card" style="border: none; box-shadow: none;">
        <div class="ls-table-scroll">
          <table class="ls-table">
            <thead>
              <tr>
                <th class="ls-th">Date</th>
                <th class="ls-th">Receipt #</th>
                <th class="ls-th">Reference #</th>
                <th class="ls-th">Type</th>
                <th class="ls-th">From (Source)</th>
                <th class="ls-th">To (Store)</th>
                <th class="ls-th">Invoices Paid</th>
                <th class="ls-th text-right">Total Paid</th>
                <th class="ls-th">Processed By</th>
                <th class="ls-th text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredRecords.length === 0">
                <td colspan="10" class="text-center p-5 text-muted" style="font-size: 16px;">
                  No payment records found matching the filters.
                </td>
              </tr>
              <tr v-for="rec in filteredRecords" :key="rec.id" class="ls-row">
                <td class="ls-td">{{ rec.payment_date }}</td>
                <td class="ls-td"><strong>{{ rec.payment_receipt || '—' }}</strong></td>
                <td class="ls-td">{{ rec.reference_number }}</td>
                <td class="ls-td">
                  <b-badge :variant="rec.entity_type === 'transfer' ? 'info' : 'primary'">
                    {{ (rec.entity_type || '').toUpperCase() }}
                  </b-badge>
                </td>
                <td class="ls-td">{{ getLocationName(rec.from_location_id) }}</td>
                <td class="ls-td"><strong>{{ getLocationName(rec.to_location_id) }}</strong></td>
                <td class="ls-td">
                  <span v-for="(inv, idx) in rec.invoices_json" :key="idx" class="invoice-chip mr-1">{{ inv.ref }}</span>
                </td>
                <td class="ls-td text-right text-success font-weight-bold">${{ formatCurrency(rec.total_paid_amount) }}</td>
                <td class="ls-td">{{ rec.processed_by }}</td>
                <td class="ls-td text-center">
                  <b-button class="ls-btn-primary" size="sm" @click="openReceipt(rec)">
                    <b-icon icon="eye"></b-icon> Preview
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- RECEIPT PREVIEW MODAL -->
    <b-modal
      id="receipt-preview-modal"
      hide-footer
      hide-header
      centered
      scrollable
      dialog-class="receipt-dialog"
      content-class="receipt-content"
    >
      <div class="pi-doc" style="display: block; position: relative;" v-if="currentReceipt">
        <button class="close-btn" @click="$bvModal.hide('receipt-preview-modal')">×</button>

        <!-- PRINT HEADER -->
        <div class="pi-banner">
          <div class="pi-banner-invoice">
            <div class="pi-hero-label">SOLD TO</div>
            <div class="pi-hero-number" style="font-size: 20px; color: #1E1B18; margin-bottom: 4px;">
              {{ getLocationName(currentReceipt.to_location_id) }}
            </div>
            <div class="pi-sub" style="font-size: 12px; line-height: 1.4;">
              {{ getLocationAddress(currentReceipt.to_location_id) || 'Store Address' }}<br>
              {{ getLocationPhone(currentReceipt.to_location_id) || 'Store Phone' }}
            </div>
          </div>

          <div class="pi-banner-brand">
            <div class="pi-brand-text">
              <div class="pi-brand-name">MECCA WHOLESALE LLC</div>
              <div class="pi-brand-line pi-brand-line--strong">101 E VENANGO STREET PHILADELPHIA, 19134</div>
              <div class="pi-brand-line">www.myfurnituremecca.com</div>
            </div>
          </div>

          <div class="pi-banner-qr" style="align-items: stretch; text-align: left; background: #fff; padding: 14px;">
            <div class="pi-hero-meta" style="grid-template-columns: 1fr; gap: 12px; margin-top: 0;">
              <div>
                <span style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: #736C63;">Date</span>
                <b style="font-size: 14px; display: block; margin-top: 2px;">{{ currentReceipt.payment_date }}</b>
              </div>
              <div>
                <span style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: #736C63;">Receipt #</span>
                <b style="font-size: 14px; display: block; margin-top: 2px; text-decoration: underline;">{{ currentReceipt.payment_receipt || '—' }}</b>
              </div>
              <div>
                <span style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: #736C63;">Reference #</span>
                <b style="font-size: 14px; display: block; margin-top: 2px;">{{ currentReceipt.reference_number }}</b>
              </div>
            </div>
          </div>
        </div>

        <!-- ITEMS TABLE -->
        <div class="pi-card">
          <h2 class="pi-title">Order details</h2>
          <div class="pi-plist-head">
            <span>Invoice #</span>
            <span>Item #</span>
            <span>Description</span>
            <span class="text-center">Qty</span>
            <span class="text-right">Price</span>
            <span class="text-right">Total</span>
          </div>

          <div class="pi-plist">
            <template v-for="inv in currentReceipt.invoices_json">
              <div v-for="(item, idx) in inv.items" :key="inv.id + '-' + idx" class="pi-pcard">
                <div class="pi-pdesc">
                  <div class="pi-kicker" v-if="idx === 0">{{ inv.ref }}</div>
                </div>
                <div class="pi-pdesc">
                  <div class="pi-pname">{{ (item.product_sku || item.sku || '').toUpperCase() }}</div>
                </div>
                <div class="pi-pdesc">
                  <div class="pi-pname">{{ (item.product_name || item.name || '').toUpperCase() }}</div>
                </div>
                <div class="pi-pprice text-center">
                  <b>{{ item.quantity }}</b>
                </div>
                <div class="pi-pdel text-right">
                  <b>${{ formatCurrency(item.unit_price || item.wholesale_price || item.price) }}</b>
                </div>
                <div class="pi-ptotal text-right">
                  <b class="pi-total-amt">${{ formatCurrency((item.quantity || 0) * (item.unit_price || item.wholesale_price || item.price || 0)) }}</b>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="pi-card pi-totals" style="grid-template-columns: 1fr; text-align: center;">
          <div class="pi-col" style="border: none;">
            <h3>Receipt Summary</h3>
            <div class="pi-row" style="justify-content: center; font-size: 18px; margin-top: 20px;">
              <span>TOTAL PAID:</span>
              <b style="font-size: 28px; color: #0F6E6B; margin-left: 10px;">${{ formatCurrency(currentReceipt.total_paid_amount) }}</b>
            </div>
            <div class="pi-row" style="justify-content: center; margin-top: 10px;">
              <span>Payment Type:</span>
              <b>CHECK / TRANSFER</b>
            </div>

            <!-- 👇 NEW: Comments -->
            <div v-if="currentReceipt.comments && currentReceipt.comments.trim()" class="pi-row" style="justify-content: center; margin-top: 10px; text-align: left;">
              <span>Comments:</span>
              <b style="max-width: 60%; word-break: break-word;">{{ currentReceipt.comments }}</b>
            </div>

            <div style="margin-top: 30px;">
              <b-button variant="dark" @click="printReceipt" style="padding: 12px 30px; font-size: 16px;">
                <b-icon icon="printer"></b-icon> Print Receipt
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';

export default {
  name: "PaidInvoices",
  components: { DatePicker },
  data() {
    return {
      packet: {},
      loading: false,
      records: [],
      currentReceipt: null,

      // Filters
      selectedDateRange: null,
      selectedType: null,
      selectedFromLocation: null,
      selectedToLocation: null,
      searchReceipt: '',       // 👈 NEW
      searchInvoiceId: '',
      searchReference: '',
    };
  },
  computed: {
    totalCollected() {
      return this.filteredRecords.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0);
    },
    typeOptions() {
      const types = new Set(this.records.map(r => String(r.entity_type || '').toLowerCase()));
      const options = [{ value: null, text: 'All Types' }];
      if (types.has('transfer')) options.push({ value: 'transfer', text: 'Transfer' });
      if (types.has('customer')) options.push({ value: 'customer', text: 'Customer' });
      return options;
    },
    fromLocationOptions() {
      const ids = [...new Set(this.records.map(r => String(r.from_location_id)))];
      const locs = this.packet.locations || [];
      const matched = locs.filter(l => ids.includes(String(l.value)));
      const hasWarehouse = ids.some(id => ['0', '40', '41', '42'].includes(id));
      const options = [{ value: null, text: 'All Sources' }];
      if (hasWarehouse) options.push({ value: '0', text: 'MECCA WHOLESALE' });
      matched
        .filter(l => !['0', '40', '41', '42'].includes(String(l.value)))
        .forEach(s => options.push({ value: s.value, text: s.text }));
      return options;
    },
    toLocationOptions() {
      const ids = [...new Set(this.records.map(r => String(r.to_location_id)))];
      const locs = this.packet.locations || [];
      const matched = locs.filter(l => ids.includes(String(l.value)));
      const hasWarehouse = ids.some(id => ['0', '40', '41', '42'].includes(id));
      const options = [{ value: null, text: 'All Stores' }];
      if (hasWarehouse) options.push({ value: '0', text: 'MECCA WHOLESALE' });
      matched
        .filter(l => !['0', '40', '41', '42'].includes(String(l.value)))
        .forEach(s => options.push({ value: s.value, text: s.text }));
      return options;
    },
    filteredRecords() {
      return this.records.filter(rec => {
        // 1. Date Range
        if (this.selectedDateRange && this.selectedDateRange.length === 2 && this.selectedDateRange[0]) {
          const start = moment(this.selectedDateRange[0], 'MM-DD-YYYY').startOf('day');
          const end = moment(this.selectedDateRange[1], 'MM-DD-YYYY').endOf('day');
          const recDate = moment(rec.payment_date, [
            'MM-DD-YYYY HH:mm:ss A',
            'MM-DD-YYYY HH:mm A',
            'MM-DD-YYYY',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD',
          ], true);
          if (recDate.isValid() && !recDate.isBetween(start, end, null, '[]')) {
            return false;
          }
        }

        // 2. Type
        if (this.selectedType) {
          if (String(rec.entity_type || '').toLowerCase() !== String(this.selectedType).toLowerCase()) {
            return false;
          }
        }

        // 3. From
        if (this.selectedFromLocation !== null) {
          if (String(rec.from_location_id) !== String(this.selectedFromLocation)) {
            if (
              String(this.selectedFromLocation) === '0' &&
              ['0', '40', '41', '42'].includes(String(rec.from_location_id))
            ) {
              // OK
            } else {
              return false;
            }
          }
        }

        // 4. To
        if (this.selectedToLocation !== null) {
          if (String(rec.to_location_id) !== String(this.selectedToLocation)) {
            if (
              String(this.selectedToLocation) === '0' &&
              ['0', '40', '41', '42'].includes(String(rec.to_location_id))
            ) {
              // OK
            } else {
              return false;
            }
          }
        }

        // 5. Receipt # search  👈 NEW
        if (this.searchReceipt && this.searchReceipt.trim()) {
          const term = this.searchReceipt.trim().toLowerCase();
          if (!String(rec.payment_receipt || '').toLowerCase().includes(term)) {
            return false;
          }
        }

        // 6. Invoice ID search
        if (this.searchInvoiceId && this.searchInvoiceId.trim()) {
          const term = this.searchInvoiceId.trim().toLowerCase();
          const invoices = Array.isArray(rec.invoices_json) ? rec.invoices_json : [];
          const matches = invoices.some(inv =>
            String(inv.ref || '').toLowerCase().includes(term)
          );
          if (!matches) return false;
        }

        // 7. Reference #
        if (this.searchReference && this.searchReference.trim()) {
          const term = this.searchReference.trim().toLowerCase();
          if (!String(rec.reference_number || '').toLowerCase().includes(term)) {
            return false;
          }
        }

        return true;
      });
    }
  },
  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
    // Note: We DO NOT auto-load here. The parent should call `loadData()` when
    // this tab becomes active. But since this component is only mounted when the
    // Paid Invoices tab is active, it's safe to load on mount:
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP("/api/sales/get_accounting_paid_invoices/", {}, this, resolve, reject);
        });
        if (res.data && res.data.result) {
          this.records = res.data.result;
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },

    resetFilters() {
      this.selectedDateRange = null;
      this.selectedType = null;
      this.selectedFromLocation = null;
      this.selectedToLocation = null;
      this.searchReceipt = '';
      this.searchInvoiceId = '';
      this.searchReference = '';
    },

    formatCurrency(val) { return (parseFloat(val) || 0).toFixed(2); },

    getLocationName(locId) {
      if (locId === null || locId === undefined || locId === '') return '—';
      const idStr = String(locId).trim();
      if (['0', '40', '41', '42'].includes(idStr)) return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === idStr);
      return found ? found.text : `Location #${locId}`;
    },
    getLocationAddress(locId) {
      if (locId === null || locId === undefined || locId === '') return '';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(locId));
      return found ? (found.address || '') : '';
    },
    getLocationPhone(locId) {
      if (locId === null || locId === undefined || locId === '') return '';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(locId));
      return found ? (found.phone || '') : '';
    },

    openReceipt(rec) {
      this.currentReceipt = rec;
      this.$bvModal.show('receipt-preview-modal');
    },

    printReceipt() {
      window.print();
    }
  }
}
</script>

<style scoped>
/* LS PAGE STYLES */
.ls-page { background: #F6F4F0; font-family: 'Manrope', system-ui, sans-serif; color: #221F1C; font-size: 17px; }
.ls-hero { background: #221F1C; color: #fff; border-radius: 20px; overflow: hidden; border-bottom: 5px solid #EE7A22; }
.ls-hero-grid { padding: 5px 30px; }
.ls-hero-label { font-size: 15px; color: rgba(255,255,255,0.6); font-weight: 600; text-transform: uppercase; }
.ls-hero-title { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin: 4px 0 12px; display: flex; align-items: center; gap: 14px; }
.ls-count-pill { background: #EE7A22; color: #fff; font-size: 18px; font-weight: 800; padding: 5px 16px; border-radius: 999px; }
.ls-tag.is-teal { background: rgba(15,110,107,0.24); color: #7FDACF; border: 1px solid rgba(15,110,107,0.5); font-weight: 700; padding: 6px 15px; border-radius: 999px; }
.ls-btn-primary.btn { background: #EE7A22 !important; border-color: #EE7A22 !important; color: #fff !important; border-radius: 10px !important; font-weight: 700; padding: 6px 12px; font-size: 14px; }
.ls-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; }
.ls-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.ls-th { background: #221F1C; color: #fff; text-align: left; font-size: 15px; font-weight: 700; text-transform: uppercase; padding: 18px 14px; border-bottom: 3px solid #EE7A22; }
.ls-td { padding: 16px 14px; border-bottom: 1px solid #F1EEE9; }
.ls-row:hover td { background: #FAF9F7; }
.invoice-chip { display: inline-block; background: #E5E1DA; color: #221F1C; padding: 4px 10px; border-radius: 6px; font-size: 13px; font-weight: 700; }

/* Datepicker style */
:deep(.mx-datepicker) { width: 100%; }
:deep(.mx-input) {
  height: 34px !important;
  font-size: 14px !important;
  border-radius: 8px !important;
  border: 1.5px solid #E5E1DA !important;
  text-align: left;
}

/* ========================================== */
/* PRINT INVOICE (PI) STYLES                  */
/* ========================================== */
.receipt-content {
  border: none;
  border-radius: 4px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
  overflow: hidden;
}
.close-btn { position: absolute; top: 15px; right: 20px; background: none; border: none; font-size: 28px; color: #999; cursor: pointer; z-index: 10; }
.close-btn:hover { color: #000; }

.pi-doc { font-family: 'Manrope', Arial, sans-serif; color: #1E1B18; background: #fff; padding: 24px; }
.pi-banner { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr); align-items: center; gap: 18px; padding: 14px 18px; border-radius: 14px; background: #FFF; border: 1.5px solid #E2DED8; border-bottom: 4px solid #EE7A22; margin-bottom: 20px; }
.pi-banner-invoice { padding-right: 10px; border-right: 1px solid #E2DED8; }
.pi-hero-label { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #736C63; }
.pi-hero-number { font-family: 'Barlow Condensed', sans-serif; font-size: 26px; font-weight: 600; color: #D4620F; }
.pi-hero-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px; }
.pi-hero-meta span { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #736C63; }
.pi-hero-meta b { font-size: 13px; display: block; margin-top: 2px; }

.pi-banner-brand { text-align: center; border-right: 1px solid #E2DED8; }
.pi-brand-name { font-family: 'Barlow Condensed', sans-serif; font-size: 24px; font-weight: 800; color: #1E1B18; margin-bottom: 4px; }
.pi-brand-line { font-size: 13px; color: #736C63; }
.pi-brand-line--strong { color: #1E1B18; font-weight: 700; }

.pi-banner-qr { display: flex; flex-direction: column; align-items: center; text-align: center; background: #F6F4F0; border: 1px solid #E2DED8; border-radius: 12px; padding: 8px; }
.pi-qr-title { font-family: 'Barlow Condensed', sans-serif; font-size: 13px; font-weight: 800; color: #D4620F; text-transform: uppercase; }
.pi-qr-sub { font-size: 9px; color: #4A4540; }

.pi-card { background: #F4F2EF; border: 1px solid #E2DED8; border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; }
.pi-title { font-family: 'Barlow Condensed', sans-serif; font-size: 16px; font-weight: 600; text-transform: uppercase; margin: 0 0 12px; padding-left: 10px; border-left: 4px solid #EE7A22; line-height: 1; }

.pi-plist-head { display: grid; grid-template-columns: 1fr 1.2fr 2fr 0.5fr 0.8fr 0.8fr; gap: 10px; background: #1E1B18; color: #fff; border-radius: 8px; padding: 8px 12px; font-size: 10px; font-weight: 800; text-transform: uppercase; margin-bottom: 8px; }
.pi-pcard { display: grid; grid-template-columns: 1fr 1.2fr 2fr 0.5fr 0.8fr 0.8fr; gap: 10px; background: #fff; border: 1px solid #E2DED8; border-radius: 8px; padding: 10px 12px; margin-bottom: 6px; align-items: center; }
.pi-pname { font-size: 13px; font-weight: 800; }
.pi-sub { font-size: 11px; color: #736C63; }
.pi-prow { display: flex; justify-content: space-between; font-size: 12px; }
.pi-total-amt { font-size: 14px; font-weight: 800; color: #D4620F; }

.pi-totals { display: grid; grid-template-columns: 1fr; padding: 20px; background: #F4F2EF; gap: 30px; width: 34%; }
.pi-col h3 { font-family: 'Barlow Condensed', sans-serif; font-size: 20px; font-weight: 800; text-transform: uppercase; margin: 0 0 12px; padding-bottom: 6px; border-bottom: 2px solid #EE7A22; }
.pi-row { display: flex; align-items: baseline; gap: 8px; font-size: 14px; padding: 4px 0; }
.pi-row span { font-weight: 600; color: #736C63; }
.pi-row i { flex: 1; border-bottom: 1px dotted #A9A29A; }
.pi-row b { font-weight: 800; }

@media (max-width: 767px) {
  .pi-banner { grid-template-columns: 1fr; text-align: center; }
  .pi-banner-invoice { border-right: none; border-bottom: 1px solid #E2DED8; padding-bottom: 10px; }
  .pi-banner-brand { border-right: none; border-bottom: 1px solid #E2DED8; padding-bottom: 10px; }
}
</style>

<style>
.receipt-dialog {
  max-width: 1000px !important;
  width: 90% !important;
  margin: 1.75rem auto !important;
}
@media (max-width: 767px) {
  .receipt-dialog {
    width: 100% !important;
    margin: 0 !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Accounting/PaidInvoices.vue